export interface JwtPayload {
  iss?: string
  exp?: number
  realm_access?: { roles?: string[] }
}

function isJwtPayload(value: unknown): value is JwtPayload {
  if (typeof value !== 'object' || value === null) return false
  const v = value as Record<string, unknown>
  if ('iss' in v && typeof v.iss !== 'string') return false
  if ('exp' in v && typeof v.exp !== 'number') return false
  if ('realm_access' in v) {
    const ra = v.realm_access
    if (typeof ra !== 'object' || ra === null) return false
    const roles = (ra as Record<string, unknown>).roles
    if (roles !== undefined && !Array.isArray(roles)) return false
  }
  return true
}

/**
 * Decodes the payload section of a JWT (base64url) without verifying the signature.
 * Signature verification is the server's responsibility.
 * Returns null if the token is malformed or unparseable.
 */
export function parseJwtPayload(token: string): JwtPayload | null {
  if (!token) return null

  const parts = token.split('.')
  if (parts.length !== 3) return null

  try {
    const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/')
    const padded = base64.padEnd(
      base64.length + ((4 - (base64.length % 4)) % 4),
      '=',
    )
    // atob decodes base64 to a Latin-1 binary string, not UTF-8. JWT claims can contain
    // non-ASCII characters (e.g. accented names, international email addresses). Passing the
    // raw atob output to JSON.parse would either corrupt multi-byte characters or throw.
    // Percent-encoding each byte and then decodeURIComponent re-interprets the bytes as UTF-8.
    const decoded = decodeURIComponent(
      atob(padded)
        .split('')
        .map(c => '%' + c.charCodeAt(0).toString(16).padStart(2, '0'))
        .join(''),
    )
    const parsed: unknown = JSON.parse(decoded)
    return isJwtPayload(parsed) ? parsed : null
  } catch {
    return null
  }
}

/**
 * Returns true if the token belongs to a staff user.
 * Staff tokens must satisfy all three conditions:
 *  1. The `iss` claim exactly matches `${keycloakBaseUrl}/realms/${staffRealm}` — validates both
 *     the expected Keycloak host and realm, preventing tokens from other Keycloak instances
 *     that happen to share the same realm name from passing.
 *  2. `realm_access.roles` includes `'support'` — the only role that grants staff access.
 *     `'support-admin'` does not, and neither does any other role. It exists in the
 *     `management` realm for historical reasons and confers no distinct privilege;
 *     the backend is dropping it from GIM_STAFF_REALM_REQUIRED_ROLES so that both
 *     sides agree on `'support'` alone (KPS-534).
 *  3. The `exp` claim is in the future (token is not expired).
 *
 * Note: signature verification is the server's responsibility — this only checks claims client-side.
 */
export function isStaffToken(
  token: string,
  staffRealm: string,
  keycloakBaseUrl: string,
): boolean {
  if (!staffRealm || !keycloakBaseUrl) return false

  const payload = parseJwtPayload(token)
  if (!payload) return false

  const expectedIssuer = `${keycloakBaseUrl.replace(/\/$/, '')}/realms/${staffRealm}`
  const issMatches =
    typeof payload.iss === 'string' && payload.iss === expectedIssuer

  const hasSupport =
    Array.isArray(payload.realm_access?.roles) &&
    payload.realm_access.roles.includes('support')

  const isNotExpired =
    typeof payload.exp === 'number' && payload.exp * 1000 > Date.now()

  return issMatches && hasSupport && isNotExpired
}
